/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef } from "react";
import BrandLogo from "./components/common/BrandLogo";
import HoneyReturnWall from "./HoneyReturnWall";
import CancelledWall from "./CancelledWall";
import FollowerGate from "./components/onboarding/FollowerGate";
import WorkspaceDashboard from "./WorkspaceDashboard";
import TeamsUpsell from "./components/teams/TeamsUpsell";
import WorkspaceEmptyState from "./components/teams/WorkspaceEmptyState";
import AgencyLayout from "./agency/AgencyLayout";
import AgencyDashboard from "./agency/AgencyDashboard";
import AgencyClientList from "./agency/AgencyClientList";
import AgencyClientDetail from "./agency/AgencyClientDetail";
import AgencyInviteForm from "./agency/AgencyInviteForm";
import AgencySettings from "./agency/AgencySettings";
import AgencyReports from "./agency/AgencyReports";
import AgencyOnboard from "./agency/AgencyOnboard";
import AgencyMembers from "./agency/AgencyMembers";
import AgencyWorkflows from "./agency/AgencyWorkflows";
import LoginPage from "./components/auth/LoginPage";
import InviteAcceptPage from "./components/workspace/InviteAcceptPage";
import InviteOnboarding from "./components/workspace/InviteOnboarding";
import NavBar from "./components/layout/NavBar";
import LinkedInPreviewCard from "./components/posts/LinkedInPreviewCard";
import CreatePostModal from "./components/posts/CreatePostModal";
import { BusyBee, MetricCard, BeeChart, ActivityDashboard, ComplianceChecker, PLUG_TYPES, PLUG_TONES, PLUG_QUESTIONS_MAP } from "./components/common/SharedComponents";
import { DashTour, SettingsTour } from "./components/common/TourOverlay";
import PlugPostPanel from "./components/posts/PlugPostPanel";
import AuthorityPresenceCard from "./components/evergreen/AuthorityPresenceCard";
import PostsPage from "./components/posts/PostsPage";
import DashboardPage from "./components/analytics/DashboardPage";
import SettingsPage from "./components/settings/SettingsPage";

const API_BASE = process.env.REACT_APP_API_URL || "https://go.brandhives.com";

// ─── Design tokens ────────────────────────────────────────────────────────────
const G = "#0077B5";
const GD = "#005885";
const BG = "#F3F2EF";
const CARD = "#FFFFFF";
const B = "#E1E9EE";
const B2 = "#DDEAF0";
const GRN = "#057642";
const GRN2 = "#F0FAF4";
const RED = "#C0392B";
const RED2 = "#FEF2F2";

const TONE_COLORS = {
  Professional:   "#0077B5",
  Conversational: "#7C3AED",
  Bold:           "#DC2626",
  Educational:    "#059669",
  Storytelling:   "#D97706",
};

// ─── Mock data ────────────────────────────────────────────────────────────────
const USER = {
  name: "Jane",
  email: "jane@company.com",
  plan: "Presence",
  price: "$99",
  tone: "Professional",
  mode: "review",
  reviewDay: "Monday",
  reviewHour: "8:00 am",
  reviewTz: "Eastern (ET)",
  nextBilling: "April 8, 2026",
  trialEnds: "April 8, 2026",
  topics: ["Business & Entrepreneurship", "Real Estate", "Health & Wellness"],
  linkedin: "Jane Smith · linkedin.com/in/janesmith",
};

function fmtDay(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString("en-CA", { weekday: "short", month: "short", day: "numeric" });
}
function fmtDate(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString("en-CA", { month: "short", day: "numeric" });
}

const ANALYTICS = []; // Populated from API

const INVOICES = [
  { date: "Mar 8, 2026", amount: "$0.00", desc: "Trial period", status: "paid" },
  { date: "Feb 8, 2026", amount: "$99.00", desc: "Presence plan", status: "paid" },
  { date: "Jan 8, 2026", amount: "$99.00", desc: "Presence plan", status: "paid" },
];

const MBTI_TYPES = [
  { type: "INTJ", label: "Architect"    }, { type: "INTP", label: "Logician"     },
  { type: "ENTJ", label: "Commander"    }, { type: "ENTP", label: "Debater"      },
  { type: "INFJ", label: "Advocate"     }, { type: "INFP", label: "Mediator"     },
  { type: "ENFJ", label: "Protagonist"  }, { type: "ENFP", label: "Campaigner"   },
  { type: "ISTJ", label: "Logistician"  }, { type: "ISTP", label: "Virtuoso"     },
  { type: "ESTJ", label: "Executive"    }, { type: "ESTP", label: "Entrepreneur" },
  { type: "ISFJ", label: "Defender"     }, { type: "ISFP", label: "Adventurer"   },
  { type: "ESFJ", label: "Consul"       }, { type: "ESFP", label: "Entertainer"  },
];
const MBTI_DESCS = {
  INTJ: "Strategic and precise — cuts straight to the structural insight or long-term implication",
  INTP: "Intellectually curious — highlights overlooked angles, paradoxes, and underlying principles",
  ENTJ: "Commanding and decisive — leads with the strategic takeaway or business implication",
  ENTP: "Sharp and provocative — challenges conventional wisdom and flips the narrative",
  INFJ: "Purpose-driven and insightful — connects topics to deeper human truth or pattern",
  INFP: "Warm and idealistic — frames topics through a human or emotional lens",
  ENFJ: "Charismatic and people-first — ties insight to how it impacts people or teams",
  ENFP: "Energetic and imaginative — highlights exciting possibilities and human connections",
  ISTJ: "Measured and factual — references specific data, facts, or proven methods",
  ISFJ: "Genuine and helpful — frames topics around how they serve or protect others",
  ESTJ: "Confident and structured — leads with the actionable insight or operational lesson",
  ESFJ: "Friendly and inclusive — connects topics to teamwork, culture, or shared values",
  ISTP: "Concise and pragmatic — focuses on how things work or what's mechanically interesting",
  ISFP: "Understated and authentic — finds the human or aesthetic angle in every topic",
  ESTP: "Bold and high-energy — gets to the point fast, leads with what happened and why it matters",
  ESFP: "Upbeat and relatable — makes topics feel alive and relevant to everyday experience",
};
const MBTI_QUIZ = [
  { key: "ei", question: "Where do you get your energy?",
    options: [{ val: "E", emoji: "🤝", label: "From people", sub: "Energized by socializing" },
               { val: "I", emoji: "🧘", label: "From alone time", sub: "Recharge by reflecting solo" }] },
  { key: "sn", question: "How do you take in information?",
    options: [{ val: "S", emoji: "🔍", label: "Facts & details", sub: "Trust what you can observe" },
               { val: "N", emoji: "💡", label: "Patterns & ideas", sub: "Look for big-picture meaning" }] },
  { key: "tf", question: "How do you make decisions?",
    options: [{ val: "T", emoji: "⚖️", label: "Logic & objectivity", sub: "Prioritize what makes sense" },
               { val: "F", emoji: "❤️", label: "Values & empathy", sub: "Prioritize what feels right" }] },
  { key: "jp", question: "How do you approach life?",
    options: [{ val: "J", emoji: "📋", label: "Planned & structured", sub: "Prefer clear goals" },
               { val: "P", emoji: "🌊", label: "Flexible & spontaneous", sub: "Prefer to keep options open" }] },
];
const SETTING_TONES = ["Professional","Conversational","Bold","Educational","Storytelling"];

const CATEGORIES = [
  {
    id: "world_news", label: "World News", icon: "🌎", sub: "World Headlines",
    subtopics: [
      { label: "World Headlines", slugs: ["spanglishreporter.substack.com/p"] },
    ],
  },
  {
    id: "hispanic_us", label: "US Regional News", icon: "🇺🇸", sub: "US News · San Jose · El Paso · Houston",
    subtopics: [
      { label: "US News",    slugs: ["mercadonews.com/blogs/national", "spanglishusa.substack.com/p", "spanglish.ca/blogs/world-page/united-states"] },
      { label: "San Jose",   slugs: ["mercadonews.com/blogs/national/san-jose", "spanglish.ca/blogs/world-page/united-states/san-jose"] },
      { label: "El Paso",    slugs: ["mercadonews.com/blogs/national/el-paso", "spanglish.ca/blogs/world-page/united-states/el-paso"] },
      { label: "Houston",    slugs: ["spanglish.ca/blogs/world-page/united-states/houston"] },
      { label: "East Coast", slugs: ["spanglish.ca/blogs/world-page/united-states/east-coast"] },
    ],
  },
  {
    id: "canadian_regional", label: "Canadian Regional News", icon: "🍁", sub: "Canada · Toronto · BC · Montreal",
    subtopics: [
      { label: "Canada",  slugs: ["spanglishcanada.substack.com/p"] },
      { label: "Toronto", slugs: ["spanglish.ca/blogs/world-page/canada-page/toronto"] },
      { label: "BC",      slugs: ["spanglish.ca/blogs/world-page/canada-page/bc"] },
      { label: "Montreal",slugs: ["spanglish.ca/blogs/world-page/canada-page/montreal"] },
      { label: "Calgary", slugs: ["spanglish.ca/blogs/world-page/calgary"] },
    ],
  },
  {
    id: "canada_espanol", label: "Canada en Español", icon: "🇨🇦", sub: "Toronto · Calgary · BC · Montreal",
    subtopics: [
      { label: "Toronto",                    slugs: ["hispanosencanada.ca/blogs/canada/toronto"] },
      { label: "Calgary",                    slugs: ["hispanosencanada.ca/blogs/canada/calgary"] },
      { label: "British Columbia",           slugs: ["hispanosencanada.ca/blogs/canada/columbia-britanica"] },
      { label: "Montreal",                   slugs: ["hispanosencanada.ca/blogs/canada/montreal"] },
      { label: "New Brunswick & East Coast", slugs: ["hispanosencanada.ca/blogs/canada/costa-este"] },
      { label: "Vida Activa",                slugs: ["hispanosencanada.ca/blogs/rumbo"] },
      { label: "Negocio Propio",             slugs: ["hispanosencanada.ca/blogs/impulso"] },
    ],
  },
  {
    id: "real_estate", label: "Real Estate & Housing", icon: "🏠", sub: "Housing · Investment · Retirement",
    subtopics: [
      { label: "Real Estate",          slugs: ["micasita.ca/blogs/real-estate"] },
      { label: "Real Estate Insights", slugs: ["micasita.ca/blogs/real-estate-insights"] },
      { label: "Retirement Taxes",     slugs: ["micasita.ca/blogs/retirement-taxes"] },
      { label: "Retirement Homes",     slugs: ["micasita.ca/blogs/retirement-homes"] },
      { label: "Rental Property",      slugs: ["micasita.ca/blogs/rental-property"] },
      { label: "Retire Abroad",        slugs: ["micasita.ca/blogs/retire-abroad"] },
      { label: "Home Price Trends",    slugs: ["micasita.ca/blogs/home-price-trends"] },
      { label: "Housing News",         slugs: ["micasita.ca/blogs/housing-news"] },
      { label: "Los Angeles",          slugs: ["micasita.ca/blogs/los-angeles"] },
      { label: "San Jose",             slugs: ["micasita.ca/blogs/san-jose"] },
      { label: "Houston",              slugs: ["micasita.ca/blogs/houston"] },
      { label: "New York",             slugs: ["micasita.ca/blogs/new-york"] },
      { label: "New Orleans",          slugs: ["micasita.ca/blogs/new-orleans"] },
      { label: "Chicago",              slugs: ["micasita.ca/blogs/chicago"] },
      { label: "San Antonio",          slugs: ["micasita.ca/blogs/san-antonio"] },
      { label: "San Diego",            slugs: ["micasita.ca/blogs/san-diego"] },
      { label: "Dallas",               slugs: ["micasita.ca/blogs/dallas"] },
      { label: "El Paso",              slugs: ["micasita.ca/blogs/el-paso"] },
      { label: "Calgary",              slugs: ["micasita.ca/blogs/calgary"] },
      { label: "Montreal",             slugs: ["micasita.ca/blogs/montreal"] },
      { label: "Toronto",              slugs: ["micasita.ca/blogs/toronto"] },
      { label: "BC",                   slugs: ["micasita.ca/blogs/bc"] },
      { label: "Canada",               slugs: ["micasita.ca/blogs/canada"] },
      { label: "New Brunswick",        slugs: ["micasita.ca/blogs/new-brunswick"] },
      { label: "Real Estate News",     slugs: ["spanglishrealestate.substack.com/p"] },
    ],
  },
  {
    id: "legal", label: "Immigration & Law", icon: "⚖️", sub: "Immigration · Family · Business Law",
    subtopics: [
      { label: "US Immigration",     slugs: ["cozlaw.com/blogs/us-immigration-help"] },
      { label: "US Injury Law",      slugs: ["cozlaw.com/blogs/us-injury-law"] },
      { label: "US Family Law",      slugs: ["cozlaw.com/blogs/us-family-law"] },
      { label: "US Criminal Defense",slugs: ["cozlaw.com/blogs/us-criminal-defense"] },
      { label: "US Business Law",    slugs: ["cozlaw.com/blogs/us-business-law"] },
      { label: "Canada Immigration", slugs: ["cozlaw.com/blogs/canada-immigration-help"] },
      { label: "Canada Family Law",  slugs: ["cozlaw.com/blogs/canada-family-law"] },
      { label: "Canada Injury Law",  slugs: ["cozlaw.com/blogs/canada-injury-law"] },
      { label: "Canada Business Law",slugs: ["cozlaw.com/blogs/canada-business-law"] },
    ],
  },
  {
    id: "business", label: "Business & Entrepreneurship", icon: "📈", sub: "Startups · Founders · Growth",
    subtopics: [
      { label: "Founder Stories",      slugs: ["biz-index.net/blogs/founder-stories"] },
      { label: "SaaS Tools",           slugs: ["biz-index.net/blogs/saas-tools"] },
      { label: "Startup Scene",        slugs: ["biz-index.net/blogs/startup-scene"] },
      { label: "Startup & Franchise",  slugs: ["biz-index.net/blogs/startup-and-franchise"] },
      { label: "Startup News",         slugs: ["biz-index.net/blogs/startup-news"] },
      { label: "Small Business Growth",slugs: ["biz-index.net/blogs/small-business-growth"] },
      { label: "Entrepreneur Tips",    slugs: ["biz-index.net/blogs/entrepreneur-tips"] },
      { label: "Pitch Events",         slugs: ["biz-index.net/blogs/pitch-events"] },
      { label: "Legal for Small Biz",  slugs: ["biz-index.net/blogs/legal-for-small-biz"] },
      { label: "Business Insurance",   slugs: ["biz-index.net/blogs/business-insurance"] },
      { label: "Tax & Accounting",     slugs: ["biz-index.net/blogs/tax-and-accounting"] },
      { label: "Insider Trades",       slugs: ["spanglishinsidertrades.substack.com/p"] },
      { label: "Tech Startups",        slugs: ["spanglishtechstartup.substack.com/p"] },
      { label: "Business (Substack)",  slugs: ["spanglishbusiness.substack.com/p"] },
      { label: "Markets",              slugs: ["mercadonews.com/blogs/market"] },
      { label: "Economy",              slugs: ["mercadonews.com/blogs/economy"] },
      { label: "Entrepreneurship",     slugs: ["mercadonews.com/blogs/playbook"] },
      { label: "Money",                slugs: ["mercadonews.com/blogs/money"] },
    ],
  },
  {
    id: "health_wellness", label: "Health & Wellness", icon: "🧠", sub: "Mental Health · Research · Gut Health",
    subtopics: [
      { label: "Gut Health",       slugs: ["daymed.com/blogs/gut-health"] },
      { label: "Chronic Illness",  slugs: ["daymed.com/blogs/chronic-illness"] },
      { label: "Disease Outbreaks",slugs: ["daymed.com/blogs/disease-outbreaks"] },
      { label: "Sleep & Recovery", slugs: ["daymed.com/blogs/sleep-and-recovery"] },
      { label: "Health Insurance", slugs: ["daymed.com/blogs/health-insurance"] },
      { label: "Medical Research", slugs: ["daymed.com/blogs/medical-research"] },
      { label: "Mental Health",    slugs: ["daymed.com/blogs/mental-health"] },
    ],
  },
  {
    id: "lifestyle", label: "Food & Lifestyle", icon: "🌿", sub: "Recipes · Wellness · Culture · Soccer",
    subtopics: [
      { label: "Natural Remedies",  slugs: ["eat9.ca/blogs/natural-remedies"] },
      { label: "Clean Eating",      slugs: ["eat9.ca/blogs/clean-eating"] },
      { label: "Healthy Recipes",   slugs: ["eat9.ca/blogs/healthy-recipes"] },
      { label: "Detox & Gut Health",slugs: ["eat9.ca/blogs/detox-and-gut-health"] },
      { label: "Green Products",    slugs: ["eat9.ca/blogs/green-products"] },
      { label: "Organic Food",      slugs: ["eat9.ca/blogs/organic-food"] },
      { label: "Sustainable Living",slugs: ["eat9.ca/blogs/sustainable-living"] },
      { label: "Culture",           slugs: ["spanglishentertainment.substack.com/p"] },
      { label: "Soccer",            slugs: ["spanglishsoccer.substack.com/p"] },
      { label: "Living Abroad",     slugs: ["mercadonews.com/blogs/abroad"] },
    ],
  },
];

const SLUG_MAP = {};
CATEGORIES.forEach(cat => {
  cat.subtopics.forEach(sub => {
    SLUG_MAP[`${cat.id}::${sub.label}`] = sub.slugs;
  });
});

function topicForUrl(url) {
  if (!url) return null;
  const u = url.toLowerCase();
  for (const cat of CATEGORIES) {
    for (const sub of cat.subtopics) {
      if (sub.slugs.some(slug => u.includes(slug.toLowerCase()))) {
        return cat.label;
      }
    }
  }
  return null;
}

// ─── Tiny components ──────────────────────────────────────────────────────────
const Badge = ({ color, children }) => (
  <span style={{ fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 12, color, border: `1px solid ${color}33`, borderRadius: 4, padding: "2px 7px", letterSpacing: "0.08em" }}>{children}</span>
);

const Divider = () => <div style={{ height: 1, background: B, margin: "28px 0" }} />;

const SectionLabel = ({ children }) => (
  <div style={{ fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 12, letterSpacing: "0.2em", color: "#999999", textTransform: "uppercase", marginBottom: 16 }}>{children}</div>
);

// ─── Main ─────────────────────────────────────────────────────────────────────
// Agency hash router — maps #/agency/* sub-routes to components
function AgencyRouter() {
  const hash = (typeof window !== "undefined" ? window.location.hash : "").replace("#", "");
  const clientMatch = hash.match(/^\/agency\/clients\/([a-f0-9-]+)/i);

  let content;
  if (clientMatch) {
    content = <AgencyClientDetail clientId={clientMatch[1]} />;
  } else if (hash === "/agency/clients") {
    content = <AgencyClientList />;
  } else if (hash === "/agency/members") {
    content = <AgencyMembers />;
  } else if (hash === "/agency/workflows") {
    content = <AgencyWorkflows />;
  } else if (hash === "/agency/invite") {
    content = <AgencyInviteForm />;
  } else if (hash === "/agency/settings") {
    content = <AgencySettings />;
  } else if (hash === "/agency/reports") {
    content = <AgencyReports />;
  } else {
    content = <AgencyDashboard />;
  }

  return <AgencyLayout>{content}</AgencyLayout>;
}

export default function NectarApp({ initialView = "login" }) {
  const [view, setView] = useState(initialView); // login | dashboard | settings
  const [email, setEmail] = useState("");
  const [magicSent, setMagicSent] = useState(false);
  const [stagingLink, setStagingLink] = useState("");
  const [linkError, setLinkError] = useState("");
  const [page, setPage] = useState("dashboard"); // dashboard | settings | workspace
  const [plugSkipToShameless, setPlugSkipToShameless] = useState(false);
  const [workspaceId, setWorkspaceId] = useState(null);
  // Tours
  const [dashTourStep, setDashTourStep] = useState(() => {
    if (localStorage.getItem("nectar_tour_completed")) return -1;
    // Only show tour for brand-new users (no prior session)
    if (localStorage.getItem("nectar_st")) { localStorage.setItem("nectar_tour_completed", "1"); return -1; }
    return 0;
  });
  const [settingsTourStep, setSettingsTourStep] = useState(-1); // -1 = not running
  const [approvals, setApprovals] = useState({}); // pending | approved | rejected
  const [settingsTab, setSettingsTab] = useState("profile");
  const [followerCount, setFollowerCount] = useState(500);
  const [followerInput, setFollowerInput] = useState("500");
  const [followerInputDirty, setFollowerInputDirty] = useState(false);
  const [followerSaved, setFollowerSaved] = useState(false);
  const [followerHistory, setFollowerHistory] = useState([]);
  const [lastFollowerUpdate, setLastFollowerUpdate] = useState(null);
  const [postingMode, setPostingMode] = useState("review");
  const [approvedPosts, setApprovedPosts] = useState(new Set());
  const [dashCredits, setDashCredits] = useState(25);
  const [rewritingDashPostId, setRewritingDashPostId] = useState(null);
  const [customFeeds, setCustomFeeds] = useState([
    { url: "", slot: null },
    { url: "", slot: null },
    { url: "", slot: null },
  ]);
  const [rssVerifying, setRssVerifying] = useState(null); // index being verified
  const [rssVerifyResults, setRssVerifyResults] = useState({}); // { [idx]: {valid, error, feed_title, entry_count} }
  const [rssSaving, setRssSaving] = useState(false);
  const [linkStyle, setLinkStyle] = useState("comment"); // 'comment' | 'preview'
  // MBTI retake in Settings
  const [mbtiPanelOpen, setMbtiPanelOpen] = useState(false);
  const [mbtiPanelPath, setMbtiPanelPath] = useState(null); // "dropdown" | "groups" | "tone"
  const [mbtiPanelType, setMbtiPanelType] = useState(null);
  const [mbtiPanelTone, setMbtiPanelTone] = useState(null);
  const [mbtiQuizStep, setMbtiQuizStep] = useState(0);
  const [mbtiQuizAnswers, setMbtiQuizAnswers] = useState({});
  const [mbtiSaving, setMbtiSaving] = useState(false);
  const [topics, setTopics] = useState(USER.topics);
  const [tone, setTone] = useState(USER.tone);
  const [mode, setMode] = useState(USER.mode);
  const [cancelConfirm, setCancelConfirm] = useState(false);
  const [showPastPosts, setShowPastPosts] = useState(false);
  const [settingsTopicSelection, setSettingsTopicSelection] = useState({ business: "all", real_estate: "all", health_wellness: "all" });
  const [settingsTopicIds, setSettingsTopicIds] = useState(["business", "real_estate", "health_wellness"]);
  const [settingsExpandedTopic, setSettingsExpandedTopic] = useState(null);
  const [settingsTopicWarning, setSettingsTopicWarning] = useState(false);
  const [topicsSaving, setTopicsSaving] = useState(false);
  const [topicsSaved, setTopicsSaved] = useState(false);
  const [thisWeek, setThisWeek] = useState([]);
  const [pastPosts, setPastPosts] = useState([]);
  const [postsLoading, setPostsLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [profileLoadFailed, setProfileLoadFailed] = useState(false);
  const [liConnected, setLiConnected] = useState(null); // null=checking, true/false=result
  const [showComposer, setShowComposer] = useState(false);
  const [toast, setToast] = useState(null); // string | null
  const [expandedCards, setExpandedCards] = useState(new Set());
  const [editingCard, setEditingCard] = useState(null); // { id, caption }
  const [editSaving, setEditSaving] = useState(false);
  const [cardRewriting, setCardRewriting] = useState(null); // post id
  const [historySearch, setHistorySearch] = useState("");
  const [historyDateFilter, setHistoryDateFilter] = useState("all");
  const [historyStatusFilter, setHistoryStatusFilter] = useState("all");
  const [historyToneFilter, setHistoryToneFilter] = useState("all");
  const [rewriteUsage, setRewriteUsage] = useState({ remaining: null, resets_at: null });
  const [tonePopoverCard, setTonePopoverCard] = useState(null); // post id with open popover
  const [toneRewriting, setToneRewriting] = useState(null);    // post id being tone-rewritten
  const [pendingToneApply, setPendingToneApply] = useState(null); // { postId, newTone, newCaption }
  const [reschedulingCard, setReschedulingCard] = useState(null); // { id, time }
  const [deletingPost, setDeletingPost] = useState(null); // post id being deleted
  const [postEditComplianceResult, setPostEditComplianceResult] = useState(null); // compliance result for My Posts edit
  const [busyBee, setBusyBee] = useState(false);
  const [dashState, setDashState] = useState(null);
  const [dashView, setDashView] = useState("weekly"); // weekly | monthly
  const [trialBannerDismissed, setTrialBannerDismissed] = useState(() => !!sessionStorage.getItem("nectar_trial_banner_dismissed"));
  // Article swap
  const [swapPanelPostId, setSwapPanelPostId] = useState(null);
  const [swapAlternatives, setSwapAlternatives] = useState([]);
  const [swapLoading, setSwapLoading] = useState(false);
  const [swappingPostId, setSwappingPostId] = useState(null);
  const swapInFlightRef = useRef(false); // synchronous guard against double-tap
  const [swapJustDone, setSwapJustDone] = useState(null); // { postId, linkStyle }
  // Content filters (blacklist)
  const [contentFilters, setContentFilters] = useState([]);
  const [filtersLoading, setFiltersLoading] = useState(false);
  const [filterInput, setFilterInput] = useState("");
  const [filterAdding, setFilterAdding] = useState(false);
  const [dashLoading, setDashLoading] = useState(false);
  // Persist generating state in sessionStorage so it survives tab navigation
  const [generatingPosts, setGeneratingPosts] = useState(() => {
    const until = parseInt(sessionStorage.getItem("nectar_generating_until") || "0", 10);
    return Date.now() < until;
  });
  const [generateSecsLeft, setGenerateSecsLeft] = useState(0);

  // Derived plan variables (used in settings + dashboard)
  const plan = (user?.plan || dashState?.plan || "free").toLowerCase();
  const isBasic = plan === "free";
  const TIER_SLOTS = { free: 2, presence: 4, authority: 5, team: 5, agency: 5 };

  // Tick down while generating — re-reads sessionStorage each tick so the ETA update lands correctly
  useEffect(() => {
    if (!generatingPosts) { setGenerateSecsLeft(0); return; }
    const tick = () => {
      const until = parseInt(sessionStorage.getItem("nectar_generating_until") || "0", 10);
      const left = Math.max(0, Math.ceil((until - Date.now()) / 1000));
      setGenerateSecsLeft(left);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [generatingPosts]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("billing_updated") === "1") {
      setToast("Billing updated");
      const newUrl = window.location.pathname + window.location.hash;
      window.history.replaceState({}, "", newUrl);
    }
  }, []);

  useEffect(() => {
    fetch(`${API_BASE}/app/api/user/profile`, { credentials: "include" })
      .then(r => { if (!r.ok) { setProfileLoadFailed(true); return null; } return r.json(); })
      .then(data => {
        if (!data) return;
        setUser(data);
        setTone(data.tone ? data.tone.charAt(0).toUpperCase() + data.tone.slice(1) : "Professional");
        setMode(data.mode || "review");
        if (data.topics && data.topics.length) setSettingsTopicIds(data.topics);
        if (data.topic_selection && Object.keys(data.topic_selection).length) setSettingsTopicSelection(data.topic_selection);
        if (data.link_style) setLinkStyle(data.link_style);
      })
      .catch(() => {});
  }, []); // eslint-disable-line

  // LinkedIn just-connected toast + delayed background status check
  useEffect(() => {
    const justConnected = sessionStorage.getItem("nectar_li_just_connected");
    if (justConnected) {
      sessionStorage.removeItem("nectar_li_just_connected");
      setLiConnected(true);
      setToast("✓ LinkedIn connected — your hive is ready to fly");
      setTimeout(() => setToast(null), 4000);
      return; // skip background check on this load
    }
    // Reauth reconnect confirmation
    const hp = new URLSearchParams(window.location.hash.split("?")[1] || "");
    if (hp.get("reconnected") === "1") {
      setLiConnected(true);
      // Clean the URL param
      const cleanHash = window.location.hash.replace(/[&?]reconnected=1/, "");
      window.history.replaceState({}, "", window.location.pathname + cleanHash);
      // Fetch next pending post date
      fetch(`${API_BASE}/app/api/user/scheduled-posts?status=pending&limit=1`, { credentials: "include" })
        .then(r => r.ok ? r.json() : null)
        .then(data => {
          const posts = Array.isArray(data) ? data : (data?.posts || []);
          const next = posts.find(p => p.status === "pending" && p.scheduled_for);
          if (next) {
            const d = new Date(next.scheduled_for);
            const dateStr = d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
            setToast(`✓ You're reconnected. Your next post goes out ${dateStr}.`);
          } else {
            setToast("✓ You're reconnected. Your posts will resume on schedule.");
          }
          setTimeout(() => setToast(null), 6000);
        })
        .catch(() => {
          setToast("✓ You're reconnected. Your posts will resume on schedule.");
          setTimeout(() => setToast(null), 6000);
        });
      return;
    }
    // Delayed background ping — hits LinkedIn API to confirm token is live, refreshes profile data
    const t = setTimeout(() => {
      fetch(`${API_BASE}/app/api/auth/linkedin/status?ping=1`, { credentials: "include" })
        .then(r => r.ok ? r.json() : null)
        .then(s => {
          if (!s) return; // network issue — leave current state
          const live = s.healthy !== undefined ? s.healthy : s.connected;
          setLiConnected(live === true);
          // Update profile image and name if LinkedIn returned fresh data
          if (live && (s.linkedin_profile_image || s.linkedin_name)) {
            setUser(prev => prev ? {
              ...prev,
              ...(s.linkedin_profile_image ? { linkedin_profile_image: s.linkedin_profile_image } : {}),
              ...(s.linkedin_name ? { linkedin_name: s.linkedin_name } : {}),
            } : prev);
          }
        })
        .catch(() => {}); // silent failure
    }, 3000);
    return () => clearTimeout(t);
  }, []); // eslint-disable-line

  // Load saved RSS feeds whenever the RSS tab is opened
  useEffect(() => {
    if (settingsTab !== "rss") return;
    fetch(`${API_BASE}/app/api/user/rss-feeds`, { credentials: "include" })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data || !data.feeds) return;
        setCustomFeeds(prev => prev.map((_, idx) => {
          const saved = data.feeds[idx];
          if (!saved) return { url: "", slot: null };
          return { url: saved.feed_url || "", slot: saved.replaces_topic || null };
        }));
      })
      .catch(() => {});
  }, [settingsTab]); // eslint-disable-line

  useEffect(() => {
    if (!tonePopoverCard) return;
    const handler = (e) => {
      if (!e.target.closest("[data-tone-popover]")) setTonePopoverCard(null);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [tonePopoverCard]);

  useEffect(() => {
    fetch(`${API_BASE}/app/api/user/rewrite-usage`, { credentials: "include" })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setRewriteUsage({ remaining: d.rewrites_remaining, resets_at: d.resets_at }); })
      .catch(() => {});
  }, []); // eslint-disable-line

  useEffect(() => {
    fetch(`${API_BASE}/app/api/user/scheduled-posts`, { credentials: "include" })
      .then(r => r.ok ? r.json() : { posts: [] })
      .then(data => {
        const posts = data.posts || [];
        const now = new Date();
        const week = [], past = [];
        const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        posts.forEach(p => {
          const d = new Date(p.scheduled_for);
          // Skip ghost posts older than 30 days
          if (p.scheduled_for && d < thirtyDaysAgo) return;
          if (p.status === "pending" || p.status === "approved" || p.status === "expired" || p.status === "paused" || p.status === "compliance_blocked" || p.status === "compliance_failed" || d > now) {
            week.push({ id: p.id, day: fmtDay(p.scheduled_for), title: p.article_title || (p.caption || "").slice(0, 60), article_title: p.article_title, article_url: p.article_url, caption: p.caption, tone: p.tone, status: p.status, scheduled_for: p.scheduled_for, scheduled_time: p.scheduled_for, image_url: p.image_url, is_plug: p.is_plug, post_type: p.post_type });
          } else {
            past.push({ id: p.id, date: fmtDate(p.posted_at || p.scheduled_for), title: p.article_title || p.caption.slice(0, 60), article_url: p.article_url, caption: p.caption, tone: p.tone, status: p.status === "published" ? "posted" : p.status, linkedin_url: p.linkedin_url, posted_at: p.posted_at, analytics_url: p.analytics_url || null });
          }
        });
        week.sort((a, b) => new Date(a.scheduled_for) - new Date(b.scheduled_for));
        past.sort((a, b) => new Date(b.scheduled_for || 0) - new Date(a.scheduled_for || 0));
        setThisWeek(week);
        setPastPosts(past);
        setPostsLoading(false);
      })
      .catch(() => setPostsLoading(false));
  }, []); // eslint-disable-line

  useEffect(() => {
    const handler = () => setBusyBee(true);
    window.addEventListener("nectar:busy-bee", handler);
    return () => window.removeEventListener("nectar:busy-bee", handler);
  }, []);

  // Hash routing for workspace dashboard + agency routes + onboard
  useEffect(() => {
    const checkHash = () => {
      const hash = window.location.hash;
      const wsMatch = hash.match(/^#\/workspace\/([a-f0-9-]+)/i);
      if (wsMatch) {
        setWorkspaceId(wsMatch[1]);
        setPage("workspace");
        return;
      }
      if (hash.startsWith("#/agency")) {
        setPage("agency");
        return;
      }
      const joinMatch = hash.match(/^#\/join\/([A-Za-z0-9_-]+)/);
      if (joinMatch) {
        setPage("join");
        return;
      }
      if (hash === "#/my-plug" || hash === "#/weekly-override") {
        setPage("plug");
        return;
      }
      if (hash === "#/plug" || hash === "#/authority-presence") {
        setPage("authority-presence");
        return;
      }
      if (hash.startsWith("#/team")) {
        setPage("team");
        return;
      }
      if (hash.startsWith("#/invite-onboarding")) {
        setPage("invite-onboarding");
        return;
      }
      if (hash.startsWith("#/invite")) {
        setPage("invite");
        return;
      }
      if (hash === "#/dashboard") {
        setPage("dashboard");
        return;
      }
    };
    checkHash();
    window.addEventListener("hashchange", checkHash);
    return () => window.removeEventListener("hashchange", checkHash);
  }, []);

  // Fetch dashboard state when on dashboard page
  useEffect(() => {
    if (page !== "dashboard" || !user) return;
    setDashLoading(true);
    fetch(`${API_BASE}/app/api/user/dashboard-state`, { credentials: "include" })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data) {
          setDashState(data);
          setDashView(data.view === "monthly" ? "monthly" : "weekly");
          // Seed liConnected from dashboard-state only if background check hasn't resolved yet
          if (liConnected === null) {
            setLiConnected(data.linkedin_connected === true);
          }
          // Populate new dashboard state — priority: history[0] > v2_users.follower_count > 500
          const histCount = data.follower_history?.[0]?.follower_count ?? null;
          const rawCount = typeof data.follower_count === "number" ? data.follower_count : null;
          const resolvedCount = histCount ?? rawCount ?? 500;
          setFollowerCount(resolvedCount);
          setFollowerInput(String(resolvedCount));
          if (data.posting_mode) setPostingMode(data.posting_mode);
          if (data.follower_history) setFollowerHistory(data.follower_history);
          if (data.last_follower_update) setLastFollowerUpdate(data.last_follower_update);
          if (data.review_window_day) setReviewWindowDay(data.review_window_day);
          if (data.review_window_time) setReviewWindowTime(data.review_window_time);
          if (data.review_window_timezone) setReviewWindowTimezone(data.review_window_timezone);
          if (data.review_window_hours) setReviewWindowHours(data.review_window_hours);
          if (data.review_email_notify != null) setReviewEmailNotify(data.review_email_notify);
          if (data.next_review_date) setReviewWindowNextDate(data.next_review_date);
          if (data.review_deadline) setReviewDeadline(data.review_deadline);
          if (data.posts_per_week_override != null) setPostsPerWeekOverride(data.posts_per_week_override);
          if (data.is_paused != null) setIsPaused(data.is_paused);
          if (typeof data.credits_remaining === "number") setDashCredits(data.credits_remaining);
        }
      })
      .catch(() => {})
      .finally(() => setDashLoading(false));
  }, [page, user]); // eslint-disable-line

  const handleGeneratePosts = async () => {
    // Use a short window until we know post count; we'll update it after the API responds
    const until = Date.now() + 30000;
    sessionStorage.setItem("nectar_generating_until", String(until));
    setGeneratingPosts(true);
    try {
      const r = await fetch(`${API_BASE}/app/api/user/generate-posts`, { method: "POST", credentials: "include" });
      const data = await r.json();
      if (r.status === 429) {
        sessionStorage.removeItem("nectar_generating_until");
        if (data.error === "daily_limit_reached") {
          setToast(`You've used all 25 AI credits for today. Your bees will be ready again at midnight UTC — ${data.hours_until_reset} hours from now.`);
        } else if (data.error === "generation_in_progress") {
          setToast(`Still generating — check back in ${data.retry_in} seconds.`);
        } else {
          setToast(data.message || "Daily limit reached — try again tomorrow.");
        }
        setTimeout(() => setToast(null), 8000);
        setGeneratingPosts(false);
        return;
      }
      if (!r.ok) {
        sessionStorage.removeItem("nectar_generating_until");
        setToast("Something went wrong — try again");
        setTimeout(() => setToast(null), 4000);
        setGeneratingPosts(false);
        return;
      }
      // Now we know how many posts — set a realistic window (20s per post, min 30s)
      const postCount = data.posts_count || 1;
      const etaSecs = Math.max(30, postCount * 20);
      const realisticUntil = Date.now() + etaSecs * 1000;
      sessionStorage.setItem("nectar_generating_until", String(realisticUntil));
      setGenerateSecsLeft(etaSecs);

      const toast = data.partial
        ? `Generating ${data.posts_count} of ${data.posts_requested} posts — ready in about ${etaSecs}s. Resets at midnight UTC.`
        : `Generating ${data.posts_count} post${postCount !== 1 ? "s" : ""} — ready in about ${etaSecs} seconds.`;
      setToast(toast);
      setTimeout(() => setToast(null), 10000);
      // Auto-reload dashboard after ETA
      setTimeout(() => {
        sessionStorage.removeItem("nectar_generating_until");
        setGeneratingPosts(false);
        fetch(`${API_BASE}/app/api/user/dashboard-state`, { credentials: "include" })
          .then(r2 => r2.ok ? r2.json() : null)
          .then(d => { if (d) { setDashState(d); setDashView(d.view === "monthly" ? "monthly" : "weekly"); } })
          .catch(() => {});
      }, etaSecs * 1000 + 5000);
    } catch {
      sessionStorage.removeItem("nectar_generating_until");
      setToast("Something went wrong — try again");
      setTimeout(() => setToast(null), 4000);
      setGeneratingPosts(false);
    }
  };

  // Dashboard v2 helper functions
  function getGreeting() {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning";
    if (hour < 17) return "Good afternoon";
    return "Good evening";
  }

  function fmtNum(n) {
    return n ? n.toLocaleString() : "0";
  }

  const handleSaveFollowers = async () => {
    const n = parseInt(followerInput, 10);
    if (isNaN(n) || n < 0) return;
    try {
      const res = await fetch(`${API_BASE}/app/api/user/follower-count`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ follower_count: n })
      });
      const data = await res.json();
      if (data.success) {
        setFollowerCount(n);
        setFollowerInputDirty(false);
        setFollowerSaved(true);
        if (data.history) setFollowerHistory(data.history);
        setTimeout(() => setFollowerSaved(false), 2500);
      }
    } catch(e) {}
  };

  const handlePostingMode = async (mode) => {
    setPostingMode(mode);
    try {
      await fetch(`${API_BASE}/app/api/user/posting-mode`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ mode })
      });
    } catch(e) {}
  };

  const handleApprovePost = async (postId) => {
    // Optimistically update UI
    setThisWeek(w => w.map(p => p.id === postId ? { ...p, status: "approved" } : p));
    try {
      const r = await fetch(`${API_BASE}/app/api/user/approve-post`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ post_id: postId }),
      });
      if (!r.ok) {
        // Revert on failure
        setThisWeek(w => w.map(p => p.id === postId ? { ...p, status: "pending" } : p));
        setToast("Failed to approve — try again");
        setTimeout(() => setToast(null), 4000);
      }
    } catch {
      setThisWeek(w => w.map(p => p.id === postId ? { ...p, status: "pending" } : p));
      setToast("Network error — try again");
      setTimeout(() => setToast(null), 4000);
    }
  };

  const handleDashRewrite = async (post) => {
    if (dashCredits <= 0) {
      setToast("No rewrites remaining today — resets at midnight UTC");
      setTimeout(() => setToast(null), 4000);
      return;
    }
    setRewritingDashPostId(post.id);
    try {
      const r = await fetch(`${API_BASE}/app/api/user/rewrite-post`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ post_id: post.id }),
      });
      const d = await r.json();
      if (r.ok) {
        // Update caption in thisWeek (PostsPage source of truth)
        setThisWeek(w => w.map(p =>
          p.id === post.id ? { ...p, caption: d.new_caption, status: "pending" } : p
        ));
        // Also update dashState for dashboard view
        setDashState(prev => ({
          ...prev,
          upcoming_posts: (prev?.upcoming_posts || []).map(p =>
            p.id === post.id ? { ...p, caption: d.new_caption, status: "pending" } : p
          ),
        }));
        // Remove from approved set (rewrite resets back to needs review)
        setApprovedPosts(prev => { const s = new Set(prev); s.delete(post.id); return s; });
        if (typeof d.credits_remaining === "number") setDashCredits(d.credits_remaining);
        const left = d.credits_remaining ?? (dashCredits - 1);
        setToast(`✨ Rewritten — ${left} credit${left !== 1 ? "s" : ""} left today`);
        setTimeout(() => setToast(null), 4000);
      } else if (r.status === 429) {
        setDashCredits(0);
        setToast("No rewrites remaining today — resets at midnight UTC");
        setTimeout(() => setToast(null), 4000);
      } else {
        setToast(d.error || "Rewrite failed — try again");
        setTimeout(() => setToast(null), 3000);
      }
    } catch {
      setToast("Rewrite failed — try again");
      setTimeout(() => setToast(null), 3000);
    }
    setRewritingDashPostId(null);
  };

  const [disconnecting, setDisconnecting] = useState(false);
  const [disconnectConfirm, setDisconnectConfirm] = useState(false);
  const [editSchedule, setEditSchedule] = useState(false);
  const [schedDay, setSchedDay] = useState(1);
  const [schedHour, setSchedHour] = useState(8);
  const [schedTz, setSchedTz] = useState("America/Toronto");
  const [schedSaving, setSchedSaving] = useState(false);
  const [reviewWindowDay, setReviewWindowDay] = useState("Sunday");
  const [reviewWindowTime, setReviewWindowTime] = useState("9:00am");
  const [reviewWindowTimezone, setReviewWindowTimezone] = useState("America/New_York");
  const [reviewWindowHours, setReviewWindowHours] = useState(120);
  const [reviewEmailNotify, setReviewEmailNotify] = useState(true);
  const [postsPerWeekOverride, setPostsPerWeekOverride] = useState(null); // null = plan max
  const [isPaused, setIsPaused] = useState(false);
  const [postCountSaving, setPostCountSaving] = useState(false);
  const [postCountSaved, setPostCountSaved] = useState(false);
  const [reviewWindowSaving, setReviewWindowSaving] = useState(false);
  const [reviewWindowSaved, setReviewWindowSaved] = useState(false);
  const [reviewWindowNextDate, setReviewWindowNextDate] = useState(null);
  const [reviewDeadline, setReviewDeadline] = useState(null);
  const [editEmail, setEditEmail] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [confirmEmail, setConfirmEmail] = useState("");
  const [emailSaving, setEmailSaving] = useState(false);
  const [emailError, setEmailError] = useState("");

  const handleDisconnect = async () => {
    setDisconnecting(true);
    try {
      await fetch(`${API_BASE}/app/api/auth/linkedin/disconnect`, { method: "POST", credentials: "include" });
      setUser(u => u ? { ...u, linkedin_id: null, linkedin_name: null, linkedin_profile_image: null } : u);
      setLiConnected(false);
    } catch { /* ignore */ }
    setDisconnecting(false);
    setDisconnectConfirm(false);
  };

  const [reconnecting, setReconnecting] = useState(false);
  const handleReconnect = async () => {
    setReconnecting(true);
    try {
      // Get user_id from loaded user object
      const uid = user?.id;
      if (uid) {
        window.location.href = `${API_BASE}/app/api/auth/linkedin/connect?user_id=${uid}`;
        return;
      }
    } catch { /* ignore */ }
    setReconnecting(false);
  };

  const saveSchedule = async () => {
    setSchedSaving(true);
    try {
      const r = await fetch(`${API_BASE}/app/api/user/schedule`, {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ review_day: schedDay, review_hour: schedHour, review_timezone: schedTz }),
      });
      if (r.ok) {
        setUser(u => u ? { ...u, review_day: schedDay, review_hour: schedHour, review_timezone: schedTz } : u);
        setEditSchedule(false);
      }
    } catch { /* ignore */ }
    setSchedSaving(false);
  };

  const saveReviewWindow = async () => {
    setReviewWindowSaving(true);
    try {
      const r = await fetch(`${API_BASE}/app/api/user/settings`, {
        method: "PATCH", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          review_window_day: reviewWindowDay,
          review_window_time: reviewWindowTime,
          review_window_timezone: reviewWindowTimezone,
          review_window_hours: reviewWindowHours,
          review_email_notify: reviewEmailNotify,
        }),
      });
      if (r.ok) {
        const data = await r.json();
        if (data.applies_next_week && data.next_review_date) {
          const d = new Date(data.next_review_date + "T12:00:00");
          const label = d.toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" });
          setToast(`Review schedule updated. New schedule starts ${label}.`);
          setReviewWindowNextDate(data.next_review_date);
        } else {
          setReviewWindowSaved(true);
          setTimeout(() => setReviewWindowSaved(false), 2500);
        }
      }
    } catch { /* ignore */ }
    setReviewWindowSaving(false);
  };

  const saveEmail = async () => {
    setEmailError("");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)) { setEmailError("Invalid email format."); return; }
    if (newEmail !== confirmEmail) { setEmailError("Emails don't match."); return; }
    setEmailSaving(true);
    try {
      const r = await fetch(`${API_BASE}/app/api/user/email`, {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: newEmail, confirm: confirmEmail }),
      });
      const d = await r.json();
      if (r.ok) {
        setUser(u => u ? { ...u, email: d.email } : u);
        setEditEmail(false); setNewEmail(""); setConfirmEmail("");
      } else { setEmailError(d.error || "Failed to update email."); }
    } catch { setEmailError("Network error."); }
    setEmailSaving(false);
  };

  const toggleSettingsTopic = id => {
    setSettingsTopicWarning(false);
    if (settingsTopicSelection[id] !== undefined) {
      const { [id]: _, ...rest } = settingsTopicSelection;
      setSettingsTopicSelection(rest);
      setSettingsTopicIds(p => p.filter(t => t !== id));
    } else {
      if (settingsTopicIds.length >= 5) { setSettingsTopicWarning(true); return; }
      setSettingsTopicSelection(prev => ({ ...prev, [id]: "all" }));
      setSettingsTopicIds(p => [...p, id]);
    }
  };
  const toggleSettingsSubtopic = (topicId, label) => {
    if (settingsTopicSelection[topicId] === undefined) {
      if (settingsTopicIds.length >= 5) { setSettingsTopicWarning(true); return; }
      setSettingsTopicIds(p => [...p, topicId]);
    }
    setSettingsTopicSelection(prev => {
      const cur = prev[topicId];
      const curArr = cur === "all" ? [] : (Array.isArray(cur) ? cur : []);
      const newArr = curArr.includes(label) ? curArr.filter(l => l !== label) : [...curArr, label];
      return { ...prev, [topicId]: newArr.length === 0 ? "all" : newArr };
    });
  };
  const saveRssFeeds = async () => {
    setRssSaving(true);
    try {
      // Compute effective slot for each feed (explicit selection or next-available auto)
      const usedIds = new Set();
      const effectiveSlots = customFeeds.map(f => {
        if (f.slot) { usedIds.add(f.slot); return f.slot; }
        const auto = settingsTopicIds.find(id => !usedIds.has(id));
        if (auto) usedIds.add(auto);
        return auto || null;
      });
      const feeds = customFeeds
        .map((f, i) => ({ feed_url: f.url.trim(), replaces_topic: effectiveSlots[i] }))
        .filter(f => f.feed_url && f.replaces_topic);
      if (feeds.length === 0) {
        setToast("Add a feed URL before saving");
        setTimeout(() => setToast(null), 4000);
        setRssSaving(false);
        return;
      }
      const res = await fetch(`${API_BASE}/app/api/user/rss-feeds`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ feeds }),
      });
      if (res.ok) {
        setToast("RSS feeds saved");
        setTimeout(() => setToast(null), 4000);
      } else {
        setToast("Failed to save feeds — try again");
        setTimeout(() => setToast(null), 4000);
      }
    } catch {
      setToast("Failed to save feeds — try again");
      setTimeout(() => setToast(null), 4000);
    }
    setRssSaving(false);
  };

  const saveTopics = async () => {
    setTopicsSaving(true);
    try {
      await fetch(`${API_BASE}/app/api/user/topics`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ topics: settingsTopicIds, topic_selection: settingsTopicSelection, link_style: linkStyle }),
      });
      setTopicsSaved(true);
      setTimeout(() => setTopicsSaved(false), 3000);
    } catch { /* ignore */ }
    setTopicsSaving(false);
  };

  const saveCaption = async (postId, newCaption) => {
    setEditSaving(true);
    try {
      const r = await fetch(`${API_BASE}/app/api/user/scheduled-posts/${postId}`, {
        method: "PATCH", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ caption: newCaption }),
      });
      if (r.ok) {
        setThisWeek(w => w.map(p => p.id === postId ? { ...p, caption: newCaption } : p));
        setEditingCard(null);
        setPostEditComplianceResult(null);
        setToast("✓ Caption saved");
        setTimeout(() => setToast(null), 3000);
      } else {
        setToast("Failed to save — try again");
        setTimeout(() => setToast(null), 3000);
      }
    } catch {
      setToast("Failed to save — try again");
      setTimeout(() => setToast(null), 3000);
    }
    setEditSaving(false);
  };

  const changeTone = async (postId, currentCaption, newTone) => {
    setTonePopoverCard(null);
    setToneRewriting(postId);
    try {
      const r = await fetch(`${API_BASE}/app/api/user/rewrite-caption`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ caption: currentCaption, tone: newTone }),
      });
      const d = await r.json();
      if (r.ok) {
        if (typeof d.rewrites_remaining === "number") {
          setRewriteUsage(u => ({ ...u, remaining: d.rewrites_remaining }));
        }
        setPendingToneApply({ postId, newTone, newCaption: d.rewritten_caption });
        setEditingCard({ id: postId, caption: d.rewritten_caption });
      }
    } catch { /* ignore */ }
    setToneRewriting(null);
  };

  const applyToneChange = async (scope) => {
    if (!pendingToneApply) return;
    const { postId, newTone, newCaption } = pendingToneApply;
    setPendingToneApply(null);
    // Save caption + tone to this post
    try {
      await fetch(`${API_BASE}/app/api/user/scheduled-posts/${postId}`, {
        method: "PATCH", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ caption: newCaption, tone: newTone }),
      });
      setThisWeek(w => w.map(p => p.id === postId ? { ...p, caption: newCaption, tone: newTone } : p));
      setEditingCard(null);
    } catch { /* ignore */ }
    // If "all future posts", also update user default tone
    if (scope === "all") {
      try {
        await fetch(`${API_BASE}/app/api/user/tone`, {
          method: "PUT", credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ tone: newTone }),
        });
        setUser(u => u ? { ...u, tone: newTone } : u);
      } catch { /* ignore */ }
    }
  };

  const rewriteCardCaption = async (postId, caption, tone) => {
    setCardRewriting(postId);
    try {
      const r = await fetch(`${API_BASE}/app/api/user/rewrite-caption`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ caption, tone }),
      });
      const d = await r.json();
      if (r.ok) {
        setEditingCard(c => c && c.id === postId ? { ...c, caption: d.rewritten_caption } : c);
        if (typeof d.rewrites_remaining === "number") {
          setRewriteUsage(u => ({ ...u, remaining: d.rewrites_remaining }));
        }
      }
    } catch { /* ignore */ }
    setCardRewriting(null);
  };

  // ── Swap article ─────────────────────────────────────────────────────────
  const openSwapPanel = async (postId) => {
    setSwapPanelPostId(postId);
    setSwapAlternatives([]);
    setSwapLoading(true);
    try {
      const r = await fetch(`${API_BASE}/app/api/user/scheduled-posts/${postId}/alternative-articles?limit=3`, {
        credentials: "include",
      });
      const d = r.ok ? await r.json() : {};
      setSwapAlternatives(d.articles || []);
    } catch { setSwapAlternatives([]); }
    setSwapLoading(false);
  };

  const doSwapArticle = async (postId, articleUrl) => {
    // Synchronous double-tap guard — state setters are async, so two rapid
    // clicks can both pass the `disabled` check before React re-renders.
    if (swapInFlightRef.current) return;
    swapInFlightRef.current = true;
    setSwappingPostId(postId);
    try {
      const r = await fetch(`${API_BASE}/app/api/user/scheduled-posts/${postId}/swap-article`, {
        method: "PATCH", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ article_url: articleUrl }),
      });
      const d = await r.json().catch(() => ({}));
      if (r.ok && d.ok) {
        // Update rewrite credit display
        if (typeof d.rewrites_remaining === "number") {
          setRewriteUsage(u => ({ ...u, remaining: d.rewrites_remaining }));
        }
        // Reload posts to reflect new article/caption (My Posts list)
        const pr = await fetch(`${API_BASE}/app/api/user/scheduled-posts`, { credentials: "include" });
        const pd = pr.ok ? await pr.json() : {};
        if (pd.posts) {
          const now = new Date();
          setThisWeek(pd.posts.filter(p => p.status === "pending" || p.status === "rate_limited" || p.status === "approved" || new Date(p.scheduled_for) > now).map(p => ({
            id: p.id, day: p.scheduled_for, title: p.article_title || (p.caption || "").slice(0, 60), article_title: p.article_title, article_url: p.article_url, caption: p.caption, tone: p.tone, status: p.status, scheduled_for: p.scheduled_for, scheduled_time: p.scheduled_for, image_url: p.image_url,
          })));
        }
        // Also refresh dashboard state so upcoming_posts reflects the new article/caption
        fetch(`${API_BASE}/app/api/user/dashboard-state`, { credentials: "include" })
          .then(r2 => r2.ok ? r2.json() : null)
          .then(d2 => { if (d2) setDashState(d2); })
          .catch(() => {});
        // Show link style toggle instead of closing the panel
        setSwapJustDone({ postId, linkStyle: linkStyle || "comment" });
        setToast("✓ Article swapped and caption regenerated");
        setTimeout(() => setToast(null), 4000);
      } else if (r.status === 409) {
        // Already swapping — silent (the in-flight request will land)
      } else {
        setToast(d.error || "Swap failed — please try again");
        setTimeout(() => setToast(null), 3000);
      }
    } catch {
      setToast("Swap failed — please try again");
      setTimeout(() => setToast(null), 3000);
    } finally {
      setSwappingPostId(null);
      swapInFlightRef.current = false;
    }
  };

  const saveSwapLinkStyle = async (postId, ls) => {
    setSwapJustDone(s => s ? { ...s, linkStyle: ls } : s);
    try {
      await fetch(`${API_BASE}/app/api/user/scheduled-posts/${postId}`, {
        method: "PATCH", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ link_style: ls }),
      });
    } catch { /* non-critical */ }
    setSwapJustDone(null);
    setSwapPanelPostId(null);
    setSwapAlternatives([]);
  };

  // ── Content filters ────────────────────────────────────────────────────────
  const loadContentFilters = async () => {
    setFiltersLoading(true);
    try {
      const r = await fetch(`${API_BASE}/app/api/user/content-filters`, { credentials: "include" });
      const d = r.ok ? await r.json() : {};
      setContentFilters(d.filters || []);
    } catch { setContentFilters([]); }
    setFiltersLoading(false);
  };

  const addContentFilter = async () => {
    const term = filterInput.trim().toLowerCase();
    if (!term) return;
    setFilterAdding(true);
    try {
      const r = await fetch(`${API_BASE}/app/api/user/content-filters`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ term }),
      });
      const d = await r.json();
      if (r.ok) {
        setContentFilters(f => [...f, d.filter]);
        setFilterInput("");
      } else if (d.error === "upgrade_required") {
        setToast("Upgrade to Presence or higher to use content filters");
        setTimeout(() => setToast(null), 4000);
      } else if (d.error === "limit_reached") {
        setToast(d.message || "Filter limit reached for your plan");
        setTimeout(() => setToast(null), 4000);
      } else {
        setToast(d.error || "Failed to add filter");
        setTimeout(() => setToast(null), 3000);
      }
    } catch {
      setToast("Failed to add filter");
      setTimeout(() => setToast(null), 3000);
    }
    setFilterAdding(false);
  };

  const deleteContentFilter = async (filterId) => {
    setContentFilters(f => f.filter(x => x.id !== filterId));
    try {
      await fetch(`${API_BASE}/app/api/user/content-filters/${filterId}`, { method: "DELETE", credentials: "include" });
    } catch { /* best effort */ }
  };

  const pendingCount = Object.values(approvals).filter(v => v === "pending").length;
  const approvedCount = Object.values(approvals).filter(v => v === "approved").length;

  function supportRef(uid) {
    if (!uid) return "—";
    return "NCT-" + (parseInt(uid) + 5000).toString(36).toUpperCase();
  }

  // ── INVITE ONBOARDING (new user onboarding wizard, no login required) ──────
  if (page === "invite-onboarding" || (view === "login" && window.location.hash.startsWith("#/invite-onboarding"))) {
    return <InviteOnboarding />;
  }

  // ── INVITE ACCEPT (works even when not logged in) ──────────────────────────
  if (page === "invite" || (view === "login" && window.location.hash.startsWith("#/invite"))) {
    return (
      <div style={{ background: "#F3F2EF", minHeight: "100vh", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>
        <div style={{ background: "#fff", borderBottom: "1px solid #e8e8e4", padding: "14px 28px", display: "flex", alignItems: "center" }}>
          <BrandLogo size="sm" />
        </div>
        <InviteAcceptPage user={user} onAccepted={() => { window.location.hash = "/dashboard"; window.location.reload(); }} />
      </div>
    );
  }

  // ── LOGIN ──────────────────────────────────────────────────────────────────
  if (view === "login") return (
    <LoginPage onDemoLogin={() => setView("dashboard")} />
  );

  // ── SHELL ──────────────────────────────────────────────────────────────────
  return (
    <div style={{ background: BG, minHeight: "100vh", fontFamily: "'Playfair Display',Georgia,serif", color: "#1A1A1A" }}>
      {busyBee && <BusyBee onRetry={() => { setBusyBee(false); window.location.reload(); }} />}
      <style>{`:root{--blue:#0077B5;--blue-dk:#005885;--blue-lt:#EBF5FB;--bg:#F3F2EF;--card:#FFFFFF;--border:#E1E9EE;--text:#1A1A1A;--muted:#666666;--green:#057642;--green-lt:#F0FAF4;} *{box-sizing:border-box;margin:0;padding:0;} body{background:#F3F2EF;-webkit-font-smoothing:antialiased;font-family:-apple-system,'Segoe UI',Helvetica,sans-serif;color:#1A1A1A;} .mono{font-family:-apple-system,'Segoe UI',Helvetica,sans-serif;font-weight:400;} @keyframes fadeUp{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}} .fade-up{animation:fadeUp 0.38s ease forwards;} @keyframes composerSpin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}} @keyframes toastIn{from{opacity:0;transform:translateY(-16px)}to{opacity:1;transform:translateY(0)}} .row{display:flex;align-items:center;} .hover{cursor:pointer;transition:all 0.18s;} .btn-p{background:#0077B5;color:#fff;border:none;border-radius:24px;padding:12px 24px;font-size:15px;font-weight:600;cursor:pointer;min-height:44px;min-width:120px;transition:background 0.15s;font-family:-apple-system,'Segoe UI',Helvetica,sans-serif;} .btn-p:hover{background:#005885;} .btn-g{background:transparent;color:#0077B5;border:1.5px solid #0077B5;border-radius:24px;padding:12px 24px;font-size:15px;font-weight:600;cursor:pointer;min-height:44px;min-width:120px;transition:all 0.15s;font-family:-apple-system,'Segoe UI',Helvetica,sans-serif;} .btn-d{background:#dc3545;color:#fff;border:none;border-radius:8px;padding:10px 20px;font-size:15px;font-weight:600;cursor:pointer;min-height:44px;transition:background 0.15s;font-family:-apple-system,'Segoe UI',Helvetica,sans-serif;} @media (max-width: 600px) { .week-grid { grid-template-columns: 1fr 1fr !important; } } @media (max-width: 600px) { .analytics-grid { grid-template-columns: 1fr 1fr !important; } } @media (max-width: 600px) { .stats-grid { grid-template-columns: 1fr 1fr !important; } } @media (max-width: 480px) { .settings-mode-grid { grid-template-columns: 1fr !important; } } @media (max-width: 480px) { .app-footer { display: flex; flex-direction: column; align-items: center; text-align: center; } } @media (max-width: 768px) { .plug-sample-grid { grid-template-columns: 1fr !important; } } @media (max-width: 480px) { .plug-voice-grid { grid-template-columns: 1fr !important; } }`}</style>

      {/* ── DASHBOARD TOUR ── */}
      {page === "dashboard" && <DashTour step={dashTourStep} setStep={setDashTourStep} />}
      {page === "settings" && <SettingsTour step={settingsTourStep} setStep={setSettingsTourStep} setSettingsTab={setSettingsTab} />}

      {/* ── NAV ── */}
      <NavBar page={page} onPageChange={setPage} user={user} pendingCount={pendingCount} onSettingsTab={setSettingsTab} />

      <div className="app-main-wrap" style={{ maxWidth: 900, margin: "0 auto", padding: "36px 24px 48px" }}>

        {/* ════════════════════════════════════════════════════════
            DASHBOARD
        ════════════════════════════════════════════════════════ */}
        {page === "team" && (() => {
          const isWorkspacePlan = ["team", "agency", "authority_team", "pro_team"].includes(user?.plan);
          const hasWorkspace = user?.primary_workspace_id && (user?.is_workspace_member || user?.is_workspace_admin);
          const orphanState = (user?.is_workspace_member || user?.is_workspace_admin) && !user?.primary_workspace_id;

          const createWorkspace = async (name) => {
            const r = await fetch(`${API_BASE}/app/api/workspace/create`, { method: "POST", headers: { "Content-Type": "application/json", "X-Session-Token": localStorage.getItem("nectar_st") }, body: JSON.stringify({ name }) });
            const d = await r.json();
            if (r.ok && d.workspace_id) { window.location.hash = `/workspace/${d.workspace_id}`; window.location.reload(); }
            else { throw new Error(d.error || "Failed to create workspace"); }
          };

          if (isWorkspacePlan && hasWorkspace) {
            return <WorkspaceDashboard workspaceId={user.primary_workspace_id} user={user} />;
          }
          if (isWorkspacePlan && (orphanState || !hasWorkspace)) {
            return <WorkspaceEmptyState user={user} onCreateWorkspace={createWorkspace} />;
          }
          return <TeamsUpsell onUpgradeToTeam={async () => {
            const r = await fetch(`${API_BASE}/app/api/billing/checkout`, {
              method: "POST",
              headers: { "Content-Type": "application/json", "X-Session-Token": localStorage.getItem("nectar_st") },
              body: JSON.stringify({ plan: "team" }),
            });
            const d = await r.json();
            if (r.ok && d.checkout_url) { window.location.href = d.checkout_url; }
            else { throw new Error(d.error || "Failed to start checkout"); }
          }} />;
        })()}
        {page === "agency" && <AgencyRouter />}
        {page === "join" && <AgencyOnboard token={(window.location.hash.match(/^#\/join\/([A-Za-z0-9_-]+)/) || [])[1]} />}
        {page === "workspace" && workspaceId && (
          <WorkspaceDashboard workspaceId={workspaceId} user={user} onBack={() => { setPage("dashboard"); window.location.hash = "/dashboard"; }} />
        )}

        {page === "dashboard" && dashState?.requires_cancelled_wall && (
          <CancelledWall user={user} API_BASE={API_BASE} dashState={dashState} />
        )}
        {page === "dashboard" && dashState?.requires_honey_wall && !dashState?.requires_cancelled_wall && (
          <HoneyReturnWall
            user={user}
            API_BASE={API_BASE}
            onCancel={() => { /* handled internally */ }}
          />
        )}
        {page === "dashboard" && dashState && !dashState.requires_cancelled_wall && !dashState.requires_honey_wall && (dashState.follower_count === null || (!dashState.is_workspace_member && !dashState.has_plug_configured && !dashState.dashboard_welcomed && !localStorage.getItem("nectar_plug_pitch_dismissed"))) && (
          <FollowerGate
            followerInput={followerInput}
            setFollowerInput={setFollowerInput}
            handleSaveFollowers={handleSaveFollowers}
            followerSaved={followerSaved}
            isWorkspaceMember={dashState.is_workspace_member}
            hasPlugConfigured={dashState.has_plug_configured}
            onSetupPlug={() => { setPlugSkipToShameless(true); setPage("plug"); window.location.hash = "/my-plug"; }}
            onDismissPlug={() => {
              fetch(`${API_BASE}/app/api/user/dashboard-welcomed`, { method: "POST", credentials: "include" }).catch(() => {});
            }}
          />
        )}
        {page === "dashboard" && (
          <DashboardPage
            user={user} dashState={dashState} dashLoading={dashLoading}
            followerCount={followerCount} followerInput={followerInput} setFollowerInput={setFollowerInput}
            followerInputDirty={followerInputDirty} setFollowerInputDirty={setFollowerInputDirty}
            followerSaved={followerSaved} setFollowerSaved={setFollowerSaved}
            followerHistory={followerHistory} lastFollowerUpdate={lastFollowerUpdate}
            handleSaveFollowers={handleSaveFollowers} handlePostingMode={handlePostingMode}
            handleGeneratePosts={handleGeneratePosts} postingMode={postingMode}
            reviewWindowDay={reviewWindowDay} reviewWindowTime={reviewWindowTime}
            reviewWindowNextDate={reviewWindowNextDate} reviewDeadline={reviewDeadline}
            generatingPosts={generatingPosts} generateSecsLeft={generateSecsLeft}
            onNavigateSettings={(tab) => { setPage("settings"); setSettingsTab(tab); }}
            onNavigatePosts={() => setPage("posts")}
            onSetupPlug={() => { setPlugSkipToShameless(true); setPage("plug"); window.location.hash = "/my-plug"; }}
            getGreeting={getGreeting}
            dashTourStep={dashTourStep} setDashTourStep={setDashTourStep}
          />
        )}

        {page === "posts" && (
          <PostsPage
            user={user} thisWeek={thisWeek} setThisWeek={setThisWeek}
            pastPosts={pastPosts} postsLoading={postsLoading}
            editingCard={editingCard} setEditingCard={setEditingCard}
            cardRewriting={cardRewriting}
            reschedulingCard={reschedulingCard} setReschedulingCard={setReschedulingCard}
            deletingPost={deletingPost} setDeletingPost={setDeletingPost}
            swapPanelPostId={swapPanelPostId} setSwapPanelPostId={setSwapPanelPostId}
            swapAlternatives={swapAlternatives} swapLoading={swapLoading}
            swapJustDone={swapJustDone} setSwapJustDone={setSwapJustDone}
            swappingPostId={swappingPostId}
            postEditComplianceResult={postEditComplianceResult}
            setPostEditComplianceResult={setPostEditComplianceResult}
            rewriteUsage={rewriteUsage} pendingCount={pendingCount}
            openSwapPanel={openSwapPanel} doSwapArticle={doSwapArticle}
            saveSwapLinkStyle={saveSwapLinkStyle}
            handleApprovePost={handleApprovePost} handleDashRewrite={handleDashRewrite}
            postingMode={postingMode} handlePostingMode={handlePostingMode}
            onNavigateSettings={(tab) => { setPage("settings"); setSettingsTab(tab); }}
            setToast={setToast} showPastPosts={showPastPosts} setShowPastPosts={setShowPastPosts}
            onShowComposer={() => setShowComposer(true)} dashState={dashState}
            linkStyle={linkStyle} setLinkStyle={setLinkStyle}
            reviewDeadline={reviewDeadline}
          />
        )}

        {page === "plug" && (
          <div className="fade-up">
            <PlugPostPanel user={user} dashState={dashState} API_BASE={API_BASE} isTrialing={dashState?.is_trialing === true} trialDaysRemaining={dashState?.trial_days_remaining || 0} skipToShameless={plugSkipToShameless} onClearSkip={() => setPlugSkipToShameless(false)} onPageChange={setPage} />
          </div>
        )}

        {page === "authority-presence" && (
          <div className="fade-up">
            <AuthorityPresenceCard user={user} dashState={dashState} API_BASE={API_BASE} isTrialing={dashState?.is_trialing === true} />
          </div>
        )}

        {/* ════════════════════════════════════════════════════════
            SETTINGS — single scrollable page (5 sections)
        ════════════════════════════════════════════════════════ */}
        {page === "settings" && (
          <SettingsPage
            user={user}
            setUser={setUser}
            dashState={dashState}
            CATEGORIES={CATEGORIES}
            onNavigateTeam={() => setPage("team")}
            onNavigateBilling={() => {}}
            setToast={setToast}
            initialSection={settingsTab}
          />
        )}
      </div>
      <div className="app-footer" style={{ background: "#fff", borderTop: "1px solid #E1E9EE", padding: "24px 32px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 16 }}>
        <BrandLogo size="footer" showSub={true} href="https://brandhives.com" />
        <div style={{ display: "flex", gap: 24 }}>
          <a href="#/privacy-policy" style={{ fontSize: 13, color: "#999", textDecoration: "none" }}>Privacy</a>
          <a href="#/terms" style={{ fontSize: 13, color: "#999", textDecoration: "none" }}>Terms</a>
          <a href="mailto:operator@bhive.ca" style={{ fontSize: 13, color: "#999", textDecoration: "none" }}>Contact</a>
        </div>
      </div>
      <div style={{ background: "#F3F2EF", borderTop: "1px solid #E1E9EE", padding: "12px 24px", textAlign: "center" }}>
        <span style={{ fontSize: 11, color: "#999" }}>AI by Claude · Payments secured by Stripe · Developed by BHIVE™ Labs</span>
      </div>

      {/* Toast */}
      {toast && (
        <div style={{ position: "fixed", top: 20, left: "50%", transform: "translateX(-50%)", background: "#1A1A1A", color: "#fff", borderRadius: 10, padding: "13px 24px", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 15, zIndex: 2000, boxShadow: "0 8px 24px rgba(0,0,0,0.2)", animation: "toastIn 0.3s ease forwards", whiteSpace: "nowrap" }}>
          {toast}
        </div>
      )}

      {/* Composer modal */}
      {showComposer && (
        <CreatePostModal
          user={user}
          onClose={() => setShowComposer(false)}
          onSuccess={msg => {
            setShowComposer(false);
            setToast(msg);
            setTimeout(() => setToast(null), 4000);
          }}
        />
      )}
    </div>
  );
}
